import { useState, useEffect } from 'react'
import { Card, CardContent, CardHeader, CardTitle } from '../../ui/card'
import { Button } from '../../ui/button'
import { supabase } from '../../../lib/supabase'
import { toast } from 'sonner'
import { debug, debugGroup, asDebugArea } from '../../../lib/debug'
import { DEBUG, SURVEY_CONFIG } from '../../../lib/constants'
import { CreateSurveyForm } from './create-survey-form'
import { SurveyListItem } from './survey-list-item'
import { SurveyResponsesModal } from './survey-responses-modal'
import { AIProcessor } from '../../../lib/ai'
import { Loader2 } from 'lucide-react'
import { PinManagementModal } from '../../admin/pin-management-modal'
import { addHours, isBefore, differenceInHours } from 'date-fns'
import { safePropertyAccess } from '../../../lib/type-fixes'

// Define the type to match what SurveyListItem expects
interface SurveyCode {
  code: string
  organization_name: string | null
  created_at: string
  expires_at: string
  has_insights?: boolean
  total_responses?: number
}

// Extended type for our internal use which includes manager_id
interface SurveyCodeWithManagerId extends SurveyCode {
  manager_id: string
}

export function SurveyCreator() {
  const [isLoading, setIsLoading] = useState(true)
  const [surveys, setSurveys] = useState<SurveyCodeWithManagerId[]>([])
  const [showCreateForm, setShowCreateForm] = useState(false)
  const [copiedCode, setCopiedCode] = useState<string | null>(null)
  const [selectedSurvey, setSelectedSurvey] = useState<SurveyCodeWithManagerId | null>(null)
  const [showResponsesModal, setShowResponsesModal] = useState(false)
  const [roleResponses, setRoleResponses] = useState<any[]>([])
  const [isProcessing, setIsProcessing] = useState(false)
  const [processingAttempts, setProcessingAttempts] = useState(0)
  const [managerId, setManagerId] = useState<string | null>(null)
  const [surveyCount, setSurveyCount] = useState<number | null>(null)

  // Get manager ID on mount
  useEffect(() => {
    const getManagerId = async () => {
      const { data: { user } } = await supabase.auth.getUser()
      if (!user) {
        debug(asDebugArea('SURVEY_CREATOR'), 'No authenticated user found')
        return
      }

      debug(asDebugArea('SURVEY_CREATOR'), 'Authenticated user:', user.id)

      const { data: manager, error } = await supabase
        .from('survey_managers')
        .select('id')
        .eq('user_id', user.id)
        .single()

      if (error) {
        debug(asDebugArea('SURVEY_CREATOR'), 'Error getting manager ID:', error)
        return
      }

      if (manager) {
        debug(asDebugArea('SURVEY_CREATOR'), 'Found manager ID:', manager.id)
        setManagerId(manager.id)
      } else {
        debug(asDebugArea('SURVEY_CREATOR'), 'No manager record found for user:', user.id)
      }
    }

    getManagerId()
  }, [])

  // Get the current survey count
  useEffect(() => {
    const getSurveyCount = async () => {
      if (!managerId) return
  
      try {
        const { count, error } = await supabase
          .from('survey_codes')
          .select('*', { count: 'exact', head: true })
          .eq('manager_id', managerId)
          .eq('status', 'active')
  
        if (error) throw error
        setSurveyCount(count)
      } catch (error) {
        console.error('Error getting survey count:', error)
        toast.error('Failed to get survey count')
      }
    }
  
    if (managerId) {
      getSurveyCount()
    }
  }, [managerId])

  // Fetch surveys when managerId changes
  useEffect(() => {
    if (managerId) {
      fetchSurveys()
    }
  }, [managerId])

  const fetchSurveys = async () => {
    if (!managerId) return

    try {
      debug(asDebugArea('SURVEY_CREATOR'), 'Fetching surveys for manager ID:', managerId)
      
      const { data: surveyData, error } = await supabase
        .from('survey_codes')
        .select(`
          *,
          survey_insights(id)
        `)
        .eq('manager_id', managerId)
        .order('created_at', { ascending: false })

      if (error) {
        debug(asDebugArea('SURVEY_CREATOR'), 'Error fetching surveys:', error)
        throw error
      }

      // Add has_insights flag based on existence of insights
      const surveysWithInsights = surveyData?.map(survey => {
        const hasInsights = survey.survey_insights && survey.survey_insights.length > 0
        debug(asDebugArea('SURVEY_CREATOR'), `Survey ${survey.code}:`, {
          manager_id: survey.manager_id,
          has_insights: hasInsights
        })
        return {
          ...survey,
          has_insights: hasInsights
        }
      }) || []

      debugGroup(asDebugArea('SURVEY_CREATOR'), 'Fetched surveys', () => {
        console.log('Total:', surveysWithInsights.length)
        console.log('Surveys:', surveysWithInsights.map(s => ({
          code: s.code,
          manager_id: s.manager_id,
          has_insights: s.has_insights
        })))
      })

      setSurveys(surveysWithInsights)
      setSurveyCount(surveysWithInsights.filter(s => s.status === 'active').length)
    } catch (error) {
      console.error('Error fetching surveys:', error)
      toast.error('Failed to load surveys')
    } finally {
      setIsLoading(false)
    }
  }

  const handleCreateSuccess = async () => {
    setShowCreateForm(false)
    await fetchSurveys()
    
    // Get updated count after creating new survey
    if (managerId) {
      const { count, error } = await supabase
        .from('survey_codes')
        .select('*', { count: 'exact', head: true })
        .eq('manager_id', managerId)
        .eq('status', 'active')

      if (!error) {
        setSurveyCount(count)
        
        // Show warning toast if limit reached
        if (count !== null && count >= SURVEY_CONFIG.MAX_SURVEYS_PER_MANAGER) {
          toast.warning(`You have reached the maximum limit of ${SURVEY_CONFIG.MAX_SURVEYS_PER_MANAGER} active surveys`)
        }
      }
    }
  }

  const handleCopyCode = async (code: string) => {
    try {
      await navigator.clipboard.writeText(code)
      setCopiedCode(code)
      toast.success('Survey code copied to clipboard')
      setTimeout(() => setCopiedCode(null), 2000)
    } catch (error) {
      debug(asDebugArea('SURVEY_CREATOR'), 'Error copying code:', error)
      toast.error('Failed to copy code')
    }
  }

  const handleViewResponses = async (survey: SurveyCode) => {
    try {
      debugGroup(asDebugArea('SURVEY_CREATOR'), 'Loading responses', () => {
        console.log('Survey:', {
          code: survey.code,
          // Only log manager_id if it exists
          ...(surveys.find(s => s.code === survey.code)?.manager_id ? 
              { manager_id: surveys.find(s => s.code === survey.code)?.manager_id } : {})
        })
      })
      
      // Find the full survey with manager_id
      const fullSurvey = surveys.find(s => s.code === survey.code)
      if (!fullSurvey) {
        throw new Error('Survey not found')
      }
      
      // Get responses with role details
      const { data: responses, error: responsesError } = await supabase
        .from('survey_responses')
        .select(`
          role,
          custom_role_name,
          roles (
            id,
            name,
            function,
            level
          )
        `)
        .eq('survey_code', survey.code)
        .eq('status', 'completed')

      if (responsesError) throw responsesError

      debugGroup(asDebugArea('SURVEY_CREATOR'), 'Found responses', () => {
        console.log('Total:', responses?.length)
        console.log('Survey:', {
          code: survey.code,
          manager_id: fullSurvey.manager_id
        })
      })

      // Process responses into role groups
      const roleGroups = new Map()

      responses?.forEach(response => {
        const key = response.role ? `role_${response.role}` : `custom_${response.custom_role_name}`
        
        if (!roleGroups.has(key)) {
          roleGroups.set(key, {
            role_name: safePropertyAccess(response.roles, 'name') || 'Custom Role',
            role_function: safePropertyAccess(response.roles, 'function') || 'Other',
            role_level: safePropertyAccess(response.roles, 'level') || 'Other',
            custom_role_name: response.custom_role_name,
            response_count: 0
          })
        }
        
        const group = roleGroups.get(key)
        group.response_count++
      })

      debugGroup(asDebugArea('SURVEY_CREATOR'), 'Processed role groups', () => {
        console.log('Total groups:', roleGroups.size)
        console.log('Groups:', Array.from(roleGroups.values()))
      })

      // Get processing attempts and check cooldown
      const { data: insights } = await supabase
        .from('survey_insights')
        .select('created_at')
        .eq('survey_code', survey.code)
        .order('created_at', { ascending: false })
        .limit(1)

      // Check if we're in cooldown period
      if (insights && insights.length > 0) {
        const lastProcessingTime = new Date(insights[0].created_at)
        const cooldownEnd = addHours(lastProcessingTime, SURVEY_CONFIG.PROCESSING.COOLDOWN_HOURS)
        
        if (isBefore(new Date(), cooldownEnd)) {
          toast.error(`Processing cooldown period is still active. Please try again later.`)
          return
        }
      }

      // Get total processing attempts
      const { count: totalAttempts } = await supabase
        .from('survey_insights')
        .select('id', { count: 'exact', head: true })
        .eq('survey_code', survey.code)

      setProcessingAttempts(totalAttempts || 0)
      setRoleResponses(Array.from(roleGroups.values()))
      setSelectedSurvey(fullSurvey)
      setShowResponsesModal(true)
    } catch (error) {
      debug(asDebugArea('SURVEY_CREATOR'), 'Error loading responses:', error)
      toast.error('Failed to load survey responses')
    }
  }

  const handleProcessResults = async () => {
    if (!selectedSurvey) return

    // Check total attempts
    if (processingAttempts >= SURVEY_CONFIG.PROCESSING.MAX_TOTAL_ATTEMPTS) {
      toast.error(`Maximum processing attempts (${SURVEY_CONFIG.PROCESSING.MAX_TOTAL_ATTEMPTS}) reached for this survey`)
      return
    }

    // Check cooldown period
    const { data: lastInsight } = await supabase
      .from('survey_insights')
      .select('created_at')
      .eq('survey_code', selectedSurvey.code)
      .order('created_at', { ascending: false })
      .limit(1)

    if (lastInsight && lastInsight.length > 0) {
      const lastProcessingTime = new Date(lastInsight[0].created_at)
      const cooldownEnd = addHours(lastProcessingTime, SURVEY_CONFIG.PROCESSING.COOLDOWN_HOURS)
      
      if (isBefore(new Date(), cooldownEnd)) {
        toast.error(`Processing cooldown period is still active. Please try again later.`)
        return
      }
    }

    setIsProcessing(true)
    try {
      debugGroup(asDebugArea('AI_PROCESSOR'), 'Processing results', () => {
        console.log('Survey:', {
          code: selectedSurvey.code,
          manager_id: selectedSurvey.manager_id
        })
      })

      const processor = new AIProcessor()
      await processor.initialize()
      await processor.processSurvey(selectedSurvey.code)

      debug(asDebugArea('AI_PROCESSOR'), 'Results processed successfully for survey:', selectedSurvey.code)
      toast.success('Results processed successfully')
      fetchSurveys()
      setShowResponsesModal(false)
    } catch (error) {
      debug(asDebugArea('AI_PROCESSOR'), 'Error processing results:', error)
      toast.error('Failed to process results')
    } finally {
      setIsProcessing(false)
    }
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center p-8">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    )
  }

  return (
    <div className="space-y-8">
      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <div className="space-y-2">
            <CardTitle>Your Surveys</CardTitle>
            {managerId && (
              <p className="text-sm text-muted-foreground">
                Manager ID: {managerId}
              </p>
            )}
          </div>
          <Button 
            onClick={() => setShowCreateForm(!showCreateForm)}
            disabled={isLoading || (surveyCount !== null && surveyCount >= SURVEY_CONFIG.MAX_SURVEYS_PER_MANAGER)}
            title={surveyCount !== null && surveyCount >= SURVEY_CONFIG.MAX_SURVEYS_PER_MANAGER ? 
              `Maximum limit of ${SURVEY_CONFIG.MAX_SURVEYS_PER_MANAGER} active surveys reached` : 
              undefined}
          >
            {showCreateForm ? 'Cancel' : 'Create New Survey'}
          </Button>
        </CardHeader>
        {!showCreateForm && surveyCount !== null && surveyCount >= SURVEY_CONFIG.MAX_SURVEYS_PER_MANAGER && (
          <p className="text-sm text-yellow-600 dark:text-yellow-400 mt-2 px-6">
            You have reached the maximum limit of {SURVEY_CONFIG.MAX_SURVEYS_PER_MANAGER} active surveys.
            You will be able to create more when your existing surveys expire.
          </p>
        )}
        <CardContent>
          {showCreateForm ? (
            <CreateSurveyForm 
              onSuccess={handleCreateSuccess}
              onCancel={() => setShowCreateForm(false)}
            />
          ) : surveys.length === 0 ? (
            <p className="text-muted-foreground text-center py-8">
              No surveys created yet. Click the button above to create your first survey.
            </p>
          ) : (
            <div className="space-y-4">
              {surveys.map((survey) => (
                <SurveyListItem
                  key={survey.code}
                  survey={survey}
                  copiedCode={copiedCode}
                  onCopyCode={handleCopyCode}
                  onViewResponses={handleViewResponses}
                />
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {selectedSurvey && (
        <SurveyResponsesModal
          open={showResponsesModal}
          onClose={() => setShowResponsesModal(false)}
          onProcess={handleProcessResults}
          isProcessing={isProcessing}
          processingAttempts={processingAttempts}
          responses={roleResponses}
          totalResponses={selectedSurvey.total_responses ?? 0}
        />
      )}
    </div>
  )
}